import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useState } from 'react';
import backendService from '../components/backendservices/BackendService'; // Correct name
import Loader from '../components/Loader';
import { Admin, AdminFormData, ApiError, RequestData } from '../types/Admin';

const Addadmin: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [formData, setFormData] = useState<AdminFormData>({
    username: '',
    email: '',
    password: '',
    role: 'admin',
    isActive: true,
  });
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'error'>('success');
  const [userRole, setUserRole] = useState<string>('');
  const [load, setload] = useState<boolean>(false);

  useEffect(() => {
    const role = Cookies.get('role');
    setUserRole(role || '');
  }, []);

  const getAvailableRoles = () => {
    if (userRole === 'superadmin') {
      return ['admin', 'sales'];
    } else if (userRole === 'admin') {
      return ['sales'];
    }
    return [''];
  };

  const fetchAdmins = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const response = await backendService.Get(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/users?page=${page}`
      );

      setAdmins(response.data);
      setCurrentPage(response.data.currentPage);
    } catch (error: unknown) {
      console.error('Error fetching admins:', error);
      setSnackbarMessage('Error fetching admins');
      setSnackbarOpen(true);
      setSnackbarType('error');
      const apiError = error as ApiError;
      if (apiError?.response?.status === 403) {
        Cookies.remove('token');
        Cookies.remove('role');
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins(currentPage);
  }, [currentPage, fetchAdmins]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setload(true);
    try {
      const newAdmin: RequestData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        isActive: formData.isActive,
      };

      const response = await backendService.Post(
        `${process.env.REACT_APP_BASE_URL}/auth/add`,
        newAdmin
      );

      if (response.status === 200) {
        setSnackbarMessage('Admin added successfully!');
        setSnackbarOpen(true);
        setSnackbarType('success');
        setFormData({
          username: '',
          email: '',
          password: '',
          role: 'admin',
          isActive: true,
        });
        fetchAdmins(currentPage);
        setload(false);
      }
    } catch (error) {
      console.error('Error adding admin:', error);
      setSnackbarMessage('Error adding admin');
      setSnackbarOpen(true);
      setSnackbarType('error');
      setload(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAdmin) return;

    try {
      const editAdminData: RequestData = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
      };

      // Only include password if it's not empty
      if (formData.password) {
        editAdminData.password = formData.password;
      }

      const response = await backendService.Put(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/${selectedAdmin._id}`,
        editAdminData
      );

      if (response.status === 200) {
        setSnackbarMessage('Admin updated successfully!');
        setSnackbarOpen(true);
        setSnackbarType('success');
        setFormData({
          username: '',
          email: '',
          password: '',
          role: 'admin',
          isActive: true,
        });
        setSelectedAdmin(null);
        fetchAdmins(currentPage);
      }
    } catch (error) {
      console.error('Error updating admin:', error);
      setSnackbarMessage('Error updating admin');
      setSnackbarOpen(true);
      setSnackbarType('error');
    }
  };

  const handleEditClick = (admin: Admin) => {
    setSelectedAdmin(admin);
    setFormData({
      username: admin.username,
      email: admin.email,
      password: '',
      role: admin.role,
      isActive: admin.isActive,
    });
    setOpenEditDialog(true);
  };

  const handleStatusToggle = async (adminId: string, currentStatus: boolean) => {
    try {
      const role = Cookies.get('role');
      const response = await backendService.Put(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/${adminId}`,
        {
          isActive: !currentStatus,
          role: role,
        }
      );

      if (response.status === 200) {
        fetchAdmins(currentPage);
        setSnackbarMessage('Admin status updated successfully!');
        setSnackbarOpen(true);
        setSnackbarType('success');
      }
    } catch (error) {
      setSnackbarMessage('Only superadmins can update!');
      setSnackbarOpen(true);
      setSnackbarType('error');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          variant='h4'
          sx={{
            color: '#6871d1',
            fontWeight: 800,
            letterSpacing: 1,
            textShadow: '0 2px 8px rgba(104,113,209,0.10)',
          }}
        >
          Admin Management
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            backgroundColor: '#6871d1',
            color: '#fff',
            py: 1.5,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
            transition: 'all 0.3s ease',
            fontWeight: 500,
            textTransform: 'capitalize',
            px: 3,
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: '#5a63bb',
              boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
            },
            '&:disabled': {
              backgroundColor: '#aeb3e9',
              color: '#fff',
              opacity: 0.7,
            },
          }}
        >
          Add Admin
        </Button>
      </Box>

      <Card
        style={{
          background: '#2b2c3e',
          color: 'white',
          margin: '0 auto',
          // maxWidth: 1400,
          width: '100%',
          minHeight: 400,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 16,
          boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          padding: 32,
        }}
      >
        {loading ? (
          <Loader minHeight='300px' />
        ) : (
          <TableContainer
            component={Paper}
            style={{
              background: '#23234a',
              color: 'white',
              borderRadius: 12,
              boxShadow: '0 4px 24px 0 rgba(104,113,209,0.10)',
              margin: '0 auto',
              // maxWidth: 1300,
            }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ background: '#2b2c3e' }}>
                  <TableCell style={{ color: '#6871d1', fontWeight: 700 }}>Email</TableCell>
                  <TableCell style={{ color: '#6871d1', fontWeight: 700 }}>Username</TableCell>
                  <TableCell style={{ color: '#6871d1', fontWeight: 700 }}>Role</TableCell>
                  <TableCell style={{ color: '#6871d1', fontWeight: 700 }}>Status</TableCell>
                  <TableCell style={{ color: '#6871d1', fontWeight: 700 }}>Created</TableCell>
                  <TableCell style={{ color: '#6871d1', fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins?.map((admin) => (
                  <TableRow key={admin._id} style={{ background: '#1e1e2f' }}>
                    <TableCell style={{ color: '#fff' }}>{admin.email}</TableCell>
                    <TableCell style={{ color: '#fff' }}>{admin.username}</TableCell>
                    <TableCell style={{ color: '#fff' }}>{admin.role}</TableCell>
                    <TableCell>
                      <Switch
                        checked={admin.isActive}
                        onChange={() => handleStatusToggle(admin._id, admin.isActive)}
                        color='primary'
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#6871d1',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#6871d1',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ color: '#fff' }}>{formatDate(admin.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEditClick(admin)}
                        sx={{
                          backgroundColor: '#6871d1',
                          color: '#fff',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                          transition: 'all 0.3s ease',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#5a63bb',
                            boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
                          },
                          '&:disabled': {
                            backgroundColor: '#aeb3e9',
                            color: '#fff',
                            opacity: 0.7,
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          fullWidth
          maxWidth='sm'
          PaperProps={{
            style: {
              backgroundColor: '#2b2c3e',
              color: 'white',
              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
              border: '1px solid #6871d1',
              borderRadius: 16,
            },
          }}
          BackdropProps={{
            style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' },
          }}
        >
          <DialogTitle
            sx={{
              background: '#2b2c3e',
              color: '#6871d1',
              fontWeight: 700,
              fontSize: '1.2rem',
              px: 3,
              py: 2,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              fontFamily: 'Lato, sans-serif',
              textAlign: 'center',
            }}
          >
            Add New Admin
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent
              sx={{
                background: '#2b2c3e',
                p: { xs: 3, sm: 4 },
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                fontFamily: 'Lato, sans-serif',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  fontFamily: 'Lato, sans-serif',
                }}
              >
                {/* Email */}
                <TextField
                  autoFocus
                  label='Email'
                  type='email'
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  sx={{
                    input: { color: '#fff', background: '#23234a', borderRadius: 4 },
                    label: { color: '#aaa' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      '& fieldset': { borderColor: '#23234a' },
                      '&:hover fieldset': { borderColor: '#6871d1' },
                      '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                    },
                  }}
                />
                {/* Username */}
                <TextField
                  label='Username'
                  fullWidth
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  sx={{
                    input: { color: '#fff', background: '#23234a', borderRadius: 4 },
                    label: { color: '#aaa' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      '& fieldset': { borderColor: '#23234a' },
                      '&:hover fieldset': { borderColor: '#6871d1' },
                      '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                    },
                  }}
                />
                {/* Password */}
                <TextField
                  label='Password'
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleTogglePasswordVisibility} edge='end'>
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    input: { color: '#fff', background: '#23234a', borderRadius: 4 },
                    label: { color: '#aaa' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      '& fieldset': { borderColor: '#23234a' },
                      '&:hover fieldset': { borderColor: '#6871d1' },
                      '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                    },
                  }}
                />
                {/* Role */}
                <TextField
                  select
                  label='Role'
                  fullWidth
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{
                    color: '#fff',
                    label: { color: '#aaa' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      '& fieldset': { borderColor: '#23234a' },
                      '&:hover fieldset': { borderColor: '#6871d1' },
                      '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                    },
                  }}
                >
                  <option value='' disabled>
                    Select Role
                  </option>
                  {getAvailableRoles().map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </TextField>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                background: '#2b2c3e',
                px: 3,
                py: 2,
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                onClick={() => setOpenDialog(false)}
                variant='outlined'
                sx={{
                  borderColor: '#6871d1',
                  color: '#6871d1',
                  fontWeight: 700,
                  borderRadius: 4,
                  px: 2.5,
                  py: 1,
                  fontSize: '1rem',
                  textTransform: 'capitalize',
                  transition: 'all 0.3s',
                  '&:hover': {
                    backgroundColor: '#6871d1',
                    color: '#fff',
                    borderColor: '#6871d1',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                sx={{
                  backgroundColor: '#6871d1',
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: 4,
                  px: 2.5,
                  py: 1,
                  fontSize: '1rem',
                  boxShadow: 'none',
                  textTransform: 'capitalize',
                  transition: 'all 0.3s',
                  '&:hover': {
                    backgroundColor: '#5a63bb',
                    color: '#fff',
                  },
                  '&:disabled': {
                    backgroundColor: '#aeb3e9',
                    color: '#fff',
                    opacity: 0.7,
                  },
                }}
              >
                {load ? 'Adding..' : 'Add Admin'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {/* Add Edit Admin Dialog */}
        <Dialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          fullWidth
          maxWidth='sm'
          PaperProps={{
            style: {
              backgroundColor: '#2b2c3e',
              color: 'white',
              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
              border: '1px solid #6871d1',
              borderRadius: 16,
            },
          }}
        >
          <DialogTitle
            style={{ background: '#23234a', color: '#6871d1', fontWeight: 700, fontSize: '1.3rem' }}
          >
            Edit Admin
          </DialogTitle>
          <form onSubmit={handleEdit}>
            <DialogContent style={{ background: '#2b2c3e' }}>
              <TextField
                margin='dense'
                label='Email'
                type='email'
                fullWidth
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{ marginBottom: '16px' }}
              />
              <TextField
                margin='dense'
                label='Username'
                fullWidth
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                style={{ marginBottom: '16px' }}
              />
              <TextField
                margin='dense'
                label='New Password (optional)'
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge='end'>
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                style={{ marginBottom: '16px' }}
              />
              <TextField
                select
                margin='dense'
                label='Role'
                fullWidth
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                style={{ marginBottom: '16px' }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value='' disabled>
                  Select Role
                </option>
                {getAvailableRoles().map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions style={{ background: '#23234a' }}>
              <Button
                onClick={() => setOpenEditDialog(false)}
                sx={{
                  backgroundColor: 'transparent',
                  color: '#6871d1',
                  border: '1.5px solid #6871d1',
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  px: 3,
                  fontSize: '1rem',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(104,113,209,0.08)',
                    color: '#fff',
                    border: '1.5px solid #6871d1',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e3f7',
                    color: '#aaa',
                    opacity: 0.7,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                sx={{
                  backgroundColor: '#6871d1',
                  color: '#fff',
                  py: 1.5,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                  transition: 'all 0.3s ease',
                  fontWeight: 500,
                  px: 3,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: '#5a63bb',
                    boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
                  },
                  '&:disabled': {
                    backgroundColor: '#aeb3e9',
                    color: '#fff',
                    opacity: 0.7,
                  },
                }}
              >
                Update Admin
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarType}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Addadmin;
